import type { TimeRange } from "@adrekic/fieldsjson"
import { LuIcon } from "@overmap-ai/blocks"
import { fieldDataMapping, formatTime, type TimeField } from "@overmap-ai/forms"
import { use } from "react"

import { TimePickerPopover } from "../../../components/Popovers/TimePickerPopover"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { TimeFieldFilterDef } from "./TimeFieldFilterDef"

interface TimeFieldFilterCellProps extends FilterDefRenderProps<TimeFieldFilterDef> {
	field: TimeField
}

export const TimeFieldFilterCell = (props: TimeFieldFilterCellProps) => {
	const { field, filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)
	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals":
			case "before":
			case "after": {
				const modifier = filterDef.modifiers[filterModifier]
				const timeFilterValue =
					filterValue !== undefined && modifier.isFilterValueValid(filterValue) ? filterValue : undefined

				return (
					<TimePickerPopover
						value={timeFilterValue}
						onValueChange={(value) => filter.setFilterValue(value ?? undefined)}
						size={size}
					>
						<FilterCellButton className="truncate">
							{timeFilterValue ? formatTime(timeFilterValue) : "..."}
						</FilterCellButton>
					</TimePickerPopover>
				)
			}
			case "in":
			case "notIn": {
				const modifier = filterDef.modifiers[filterModifier]
				const rangeFilterValue =
					filterValue !== undefined && modifier.isFilterValueValid(filterValue) ? filterValue : undefined

				const setRangeFilterValue = (value: TimeRange) => {
					filter.setFilterValue(value.from || value.to ? value : undefined)
				}

				return (
					<>
						<TimePickerPopover
							value={rangeFilterValue?.from}
							onValueChange={(value) =>
								setRangeFilterValue({ from: value, to: rangeFilterValue?.to ?? null })
							}
							size={size}
						>
							<FilterCellButton className="truncate">
								{rangeFilterValue?.from ? formatTime(rangeFilterValue.from) : "..."}
							</FilterCellButton>
						</TimePickerPopover>

						<TimePickerPopover
							value={rangeFilterValue?.to}
							onValueChange={(value) =>
								setRangeFilterValue({ from: rangeFilterValue?.from ?? null, to: value })
							}
							size={size}
						>
							<FilterCellButton className="truncate">
								{rangeFilterValue?.to ? formatTime(rangeFilterValue.to) : "..."}
							</FilterCellButton>
						</TimePickerPopover>
					</>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge className="shrink-1 min-w-0">
				<LuIcon icon={fieldDataMapping[field.type].icon} />
				<span className="truncate">{field.label}</span>
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
